/**
 * Uebe-Modus: Lead Sheet lesen, Auto-Scroll, Schriftgroesse.
 * Gezeichnet wird ueber das Werkzeugmenue unten links.
 */
import type { View } from '../app.ts';
import { navigate, refreshDrawing } from '../app.ts';
import { getSettings, getSong, saveSettings, saveSong, songSurfaceId } from '../db/repo.ts';
import { createSheetPanel } from '../ui/sheet-panel.ts';
import { createAudioPanel } from '../ui/audio-panel.ts';
import { button, h } from '../ui/dom.ts';
import { exportSong } from '../features/pdf/dialog.ts';

const MIN_SCALE = 0.7;
const MAX_SCALE = 2.4;

export async function renderSheetView(params: Record<string, string>): Promise<View> {
  const id = params.id as string;
  const [song, settings] = await Promise.all([getSong(id), getSettings()]);
  if (!song) {
    return {
      title: 'Song nicht gefunden',
      back: '#/',
      body: h('div', { class: 'empty', text: 'Dieser Song wurde geloescht.' }),
    };
  }

  let scale = settings.fontScale;
  let current = song;

  const panel = createSheetPanel({
    song,
    scale,
    showHead: true,
    fallbackSpeed: settings.defaultScrollSpeed,
    onSpeedChange: (speed) => {
      current = { ...current, scrollSpeed: speed };
      void saveSong(current);
    },
    onTap: () => panel.scroller.toggle(),
  });

  /*
   * Referenzaufnahme, direkt am Lead Sheet. Auch hier hinzufuegbar: beim
   * Ueben soll man eine Aufnahme gleich anhaengen und abspielen koennen.
   */
  const audioPanel = createAudioPanel({
    song,
    allowAdd: true,
    onChange: async (audio) => {
      current = { ...current, audio };
      await saveSong(current);
    },
  });
  // Einmal pro Aufruf versuchen, automatisch zu starten – nie beim erneuten
  // Wischen zwischen Songs oder beim blossen Neuzeichnen der Ansicht.
  audioPanel.tryAutoplay();

  /** Schriftgroesse aendern und als Voreinstellung merken. */
  function changeScale(delta: number): void {
    scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round((scale + delta) * 20) / 20));
    panel.setScale(scale);
    void saveSettings({ ...settings, fontScale: scale });
    // Zeilen sind jetzt anders hoch – Zeichnungen neu setzen.
    requestAnimationFrame(refreshDrawing);
  }

  return {
    title: song.title || 'Ohne Titel',
    subtitle: [song.artist, song.key].filter(Boolean).join(' · ') || undefined,
    back: '#/',
    flush: true,
    body: h('div', { class: 'sheet-audio-wrap' }, audioPanel.element, panel.element),
    actions: [
      button('A−', () => changeScale(-0.1), { class: 'btn btn--ghost btn--icon', title: 'Kleiner' }),
      button('A+', () => changeScale(0.1), { class: 'btn btn--ghost btn--icon', title: 'Größer' }),
      button('Bühne', () => navigate(`#/stage/song/${song.id}`), { class: 'btn btn--ghost' }),
      button('PDF', () => exportSong(current), { class: 'btn btn--ghost', title: 'Als PDF exportieren' }),
      button('Text', () => navigate(`#/song/${song.id}`), {
        class: 'btn btn--ghost',
        title: 'Song bearbeiten',
      }),
    ],
    drawing: { id: songSurfaceId(song.id), ...panel.drawing },
    cleanup: () => {
      panel.destroy();
      audioPanel.destroy();
    },
  };
}
